import {Button, SmallLoader} from '@kit';

import React from 'react';

import {Item, ItemRarity, ItemSubType, useItemQuery, ImageTarget} from '../../../generated/types';
import {Link, buildUrl} from '../../../lib/routes';
import {PageTools} from '../../../components/page-tools';
import {getApolloError, getSourceText, wordByCount} from '../../../components/utils';
import {EditHoc} from '../../../components/edit-hoc';
import {Markdown, MarkdownInline} from '../../../components/utils/md';
import {PageTitle} from '../../../components/page-title';
import {
	decimalFractionToNormal,
	getItemDamage,
	getItemDexAcBonus,
	getItemPrice,
	getItemSubTitle,
	getItemWeight,
	getItemSubTypes,
} from '../../../components/render-helpers';
import {MainImage} from '../../../components/main-image';
import {Gallery} from '../../../components/gallery';
import {OtherTitles} from '../../../components/other-titles';
import {NotFoundSeo, isNotFoundError, useNotFound} from '../../../lib/seo';
import {linkClass} from '../../../components/utils/md/plugins/link-style';
import {ItemSeo} from '../seo';
import {NotFound} from '../../../components/not-found';
import {CommentTargetType, Comments} from '../../../components/comments';
import {VehicleStatblock} from '../../../components/vehicle-statblock';
import {RuleExcerpts} from '../../../components/rule-excerpts';

import dashboardCss from '@kit/sidebar/dashboard.module.css';
import pageCss from '../../../components/common.module.css';
import css from './item-view.module.css';
import markdownCss from '../../../components/utils/md/markdown.module.css';

type Props = {
	baseId: string;
	variantId?: string;
	title: string;
};

export const ItemView: React.FC<Props> = ({baseId, variantId, title}) => {
	const {data: baseData, loading: baseLoading, error} = useItemQuery({variables: {id: baseId}});
	const {data: variantData, loading: variantLoading} = useItemQuery({
		variables: {id: variantId!},
		skip: !variantId,
	});

	const baseEntry = baseData?.item;
	const item = (variantId ? variantData?.item : baseData?.item) as Item;
	const loading = baseLoading || variantLoading;
	const displayTitle = (variantId ? variantData?.item?.title : null) || title;
	const legacyItem = baseEntry?.legacyItem;

	const notFound = !loading && !item && isNotFoundError(error);
	useNotFound(notFound);

	const baseItem = item?.baseItem || item?.parentItem?.baseItem;
	const isShield = !!item && getItemSubTypes(item as Item).includes(ItemSubType.Shield);
	const ac = item?.ac || baseItem?.ac;
	const bonusAc = item?.bonusAc || baseItem?.bonusAc;
	const price = item && getItemPrice(item, !!variantId);

	const mainImage = item?.images?.at(0);

	return (
		<article className={dashboardCss.readingWrapper}>
			{item && (
				<ItemSeo item={item} path={variantId ? buildUrl('itemVariant', {id: baseId, variantId}) : undefined} />
			)}
			{notFound && <NotFoundSeo />}
			<PageTools />
			<PageTitle
				subTitle={
					item && (
						<em>
							<MarkdownInline text={getItemSubTitle(item)!} />
						</em>
					)
				}
			>
				{displayTitle}
			</PageTitle>
			<div className={dashboardCss.page}>
				{legacyItem && (
					<div className={css.sourceToggle}>
						<div className={css.label}>Редакция</div>
						<div>
							<Button
								shape={'circular'}
								size={'small'}
								route={'item'}
								routeParams={{id: baseId}}
								checked={!variantId}
							>
								Новая
							</Button>
						</div>
						<div>
							<Button
								shape={'circular'}
								size={'small'}
								route={'itemVariant'}
								routeParams={{id: baseId, variantId: legacyItem.id}}
								checked={!!variantId}
							>
								Старая
							</Button>
						</div>
					</div>
				)}
				{loading && (
					<div className={pageCss.fullSizeBlock}>
						<SmallLoader />
					</div>
				)}
				{notFound ? (
					<NotFound />
				) : (
					error && <div className={pageCss.fullSizeBlock}>{getApolloError(error).message}</div>
				)}
				{item && (
					<div className={css.container}>
						<div className={css.wrapper}>
							{mainImage && (
								<div className={css.image}>
									<MainImage
										image={mainImage}
										target={variantId ? undefined : {type: ImageTarget.Item, id: baseId}}
									/>
								</div>
							)}
							<div className={css.body}>
								<EditHoc
									load={() => import('../edit-buttons').then((m) => m.EditItem)}
									editProps={{id: item.id}}
								>
									{item.vehAbilities ? (
										<>
											{item.description && <Markdown text={item.description} />}
											<VehicleStatblock item={item} />
										</>
									) : (
										<div className={markdownCss.margin}>
											<div className={markdownCss.indent}>
												{price}
												{(item.weight || 0) > 0 && (
													<p>
														<b>Вес</b> {getItemWeight(item.weight!)}
													</p>
												)}
												{item.dmg1 && (
													<p>
														<b>Урон</b> {getItemDamage(item)}
													</p>
												)}
												{item.ammoItem && (
													<Markdown
														text={`*Боеприпас* {@item ${item.ammoItem.id}, ${item.ammoItem.title}}`}
													/>
												)}
												{item.range && (
													<p>
														<b>Дистанция</b> {item.range}
													</p>
												)}
												{item.reloadCount && (
													<p>
														<b>Перезарядка</b>{' '}
														{wordByCount(
															item.reloadCount,
															['выстрел', 'выстрела', 'выстрелов'],
															true,
														)}
													</p>
												)}
												{(ac || bonusAc) && (
													<p>
														<b>{!ac || isShield ? 'Увеличивает ' : ''}Класс брони </b>
														{(ac || 0) + (bonusAc || 0)}
														{getItemDexAcBonus(item)}
													</p>
												)}
												{item.strength && (
													<p>
														<b>Минимум Силы</b> {item.strength}
													</p>
												)}
												{item.vehSpeed && (
													<p>
														<b>Скорость </b>
														{decimalFractionToNormal(item.vehSpeed / 10)}{' '}
														{wordByCount(item.vehSpeed / 10, [
															'миля в час',
															'мили в час',
															'миль в час',
														])}
													</p>
												)}
												{item.crew && (
													<p>
														<b>Экипаж </b>
														{wordByCount(
															item.crew,
															['человек', 'человека', 'человек'],
															true,
														)}
													</p>
												)}
												{item.capPassenger && (
													<p>
														<b>Пассажиры </b>
														{wordByCount(
															item.capPassenger,
															['человек', 'человека', 'человек'],
															1,
														)}
													</p>
												)}
												{item.capCargo && (
													<p>
														<b>Груз </b>
														{getItemWeight(item.capCargo * 2000)}{' '}
													</p>
												)}
												{item.vehAc && (
													<p>
														<b>КБ </b>
														{item.vehAc}
													</p>
												)}
												{item.vehHp && (
													<p>
														<b>ПЗ </b>
														{item.vehHp}
													</p>
												)}
												{item.vehDmgThresh && (
													<p>
														<b>Предел прочности </b>
														{item.vehDmgThresh}
													</p>
												)}
												{item.speed && (
													<p>
														<b>Скорость </b>
														{wordByCount(item.speed, ['фут', 'фута', 'футов'], true)} (
														{wordByCount(
															item.speed / 10,
															['миля в час', 'мили в час', 'миль в час'],
															true,
														)}
														)
													</p>
												)}
												{item.carryingCapacity && (
													<p>
														<b>Грузоподъёмность </b>
														{getItemWeight(item.carryingCapacity)}
													</p>
												)}
											</div>
										</div>
									)}
									{item.parentItem && (
										<>
											<h3>
												<Link
													route={'item'}
													params={{id: item.parentItem.id}}
													className={linkClass(
														item.parentItem.rarity !== ItemRarity.None &&
															item.parentItem.rarity !== ItemRarity.Unknown
															? 'magic-item'
															: 'item',
													)}
												>
													{item.parentItem.rarity !== ItemRarity.None &&
													item.parentItem.rarity !== ItemRarity.Unknown ? (
														<i>{item.parentItem.title}</i>
													) : (
														item.parentItem.title
													)}
												</Link>
											</h3>
											{item.parentItem.description && (
												<Markdown
													text={item.parentItem.description}
													payload={{genericChildren: true}}
												/>
											)}
										</>
									)}
									{item.description && !item.vehAbilities && <Markdown text={item.description} />}
									{baseItem && (
										<>
											<h3>{baseItem.title}</h3>
											{baseItem.description && <Markdown text={baseItem.description} />}
										</>
									)}
									{item.ruleExcerpts && <RuleExcerpts excerpts={item.ruleExcerpts} />}
									{item.mountMonster && (
										<Markdown
											text={`*_Параметры скакуна:_* Этот скакун есть в списке {@monster ${item.mountMonster.id}, чудовищ}`}
										/>
									)}
									<div className={markdownCss.margin}>
										<div className={markdownCss.indent}>
											<OtherTitles
												originalTitle={item.originalTitle}
												alternativeTitles={item.alternativeTitles}
											/>
											<p>
												<b>Источник: </b>
												{getSourceText(item.source as any)}.
											</p>
										</div>
									</div>
								</EditHoc>
							</div>
							{item.images && (
								<div className={css.galleryWrapper}>
									<Gallery
										images={item.images}
										title="Галерея"
										startIndex={mainImage ? 1 : 0}
										target={variantId ? undefined : {type: ImageTarget.Item, id: baseId}}
									/>
								</div>
							)}
						</div>
					</div>
				)}
				{item && <Comments targetType={CommentTargetType.Item} targetId={baseId} />}
			</div>
		</article>
	);
};
